import { Space } from "antd";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { DataTableDateColumn } from "~components/DataTable/components/DataTableDateColumn/DataTableDateColumn.tsx";
import { DataTableSelectColumn } from "~components/DataTable/components/DataTableSelectColumn/DataTableSelectColumn.tsx";
import { DataTableStringColumn } from "~components/DataTable/components/DataTableStringColumn/DataTableStringColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import ImageView from "~components/ImageView/ImageView.tsx";
import { dateFormat } from "~constants/dateFormat.ts";
import { ImageSizes } from "~enums/imageSizes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { RosterTableColumns } from "~pages/Roster/enums/rosterTableColumns.ts";
import type { RosterDataTableRow } from "~pages/Roster/types/rosterDataTableRow.ts";
import type { UpsertRosterBaseModel } from "~pages/Roster/types/upsertRosterBaseModel";
import { findImageByTypes } from "~utilities/findImageByTypes.ts";
import { sortStringValues } from "~utils/utilities.ts";

type Props<TRow extends RosterDataTableRow> = {
  massEditMode: boolean;
  handleUpdateField: (row: TRow, update: Partial<Pick<UpsertRosterBaseModel, "bib" | "position" | "isCaptain">>) => Promise<void>;
};

export const useSharedRosterPersonColumns = <TRow extends RosterDataTableRow>({
  massEditMode,
  handleUpdateField,
}: Props<TRow>): Array<DataTableColumn<TRow>> => {
  const { formatMessage } = useIntl();
  const { sport } = useParams();
  const { rosterPositions } = useSportConfiguration();

  const positionOptions = [
    { value: null, label: "-" },
    ...rosterPositions.map((position) => ({
      value: position,
      label: formatMessage({ id: `roster.position.${sport}.${position}` }),
    })),
  ];

  type RosterRow = TRow & DataTableRow;

  return [
    {
      key: RosterTableColumns.Bib,
      title: formatMessage({ id: "roster.number" }),
      dataIndex: "bib",
      width: 70,
      sorter: (a: TRow, b: TRow) => sortStringValues(a.bib, b.bib),
      render: (value: string | null, row: RosterRow) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(bib) => handleUpdateField(row, { bib })}
        />
      ),
    },
    {
      key: RosterTableColumns.NameFullLocal,
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: ["person", "nameFullLocal"],
      width: 220,
      sorter: (a: TRow, b: TRow) => sortStringValues(a.person.nameFullLocal, b.person.nameFullLocal),
      render: (value: string, row: TRow) => (
        <Space size="small">
          <ImageView
            imageUrl={findImageByTypes(row.person?.images ?? [], [ImageTypes.PersonHead])?.url}
            size={ImageSizes.Small}
            width={25}
            height={25}
            imageType={ImageTypes.PersonHead}
          />
          {value}
        </Space>
      ),
    },
    {
      key: RosterTableColumns.PersonId,
      title: formatMessage({ id: "roster.person.id" }),
      dataIndex: "personId",
      copyable: true,
      width: 210,
    },
    {
      key: RosterTableColumns.PersonExternalId,
      title: formatMessage({ id: "person.externalId" }),
      dataIndex: ["person", "externalId"],
      copyable: true,
      width: 100,
    },
    {
      key: RosterTableColumns.PersonNameFullLatin,
      title: formatMessage({ id: "nameFullLatin" }),
      dataIndex: ["person", "nameFullLatin"],
      width: 200,
    },
    {
      key: RosterTableColumns.Dob,
      title: formatMessage({ id: "date.of.birth" }),
      dataIndex: ["person", "dob"],
      width: 100,
      render: (value: string | null) => <DataTableDateColumn value={value} format={dateFormat} />,
    },
    {
      key: RosterTableColumns.PersonGender,
      title: formatMessage({ id: "gender" }),
      dataIndex: ["person", "gender"],
      width: 90,
    },
    {
      key: RosterTableColumns.PersonNationality,
      title: formatMessage({ id: "nationality" }),
      dataIndex: ["person", "nationality"],
      width: 50,
    },
    {
      key: RosterTableColumns.PersonRepresenting,
      title: formatMessage({ id: "representing" }),
      dataIndex: ["person", "representing"],
      width: 100,
    },
    {
      key: RosterTableColumns.PersonStatus,
      title: formatMessage({ id: "person.status" }),
      dataIndex: ["person", "status"],
      width: 100,
    },
    {
      key: RosterTableColumns.PersonAdditionalDetailsHeight,
      title: formatMessage({ id: "person.additionalDetails.height" }),
      dataIndex: ["person", "additionalDetails", "height"],
      width: 30,
    },
    {
      key: RosterTableColumns.PersonAdditionalDetailsWeight,
      title: formatMessage({ id: "person.additionalDetails.weight" }),
      dataIndex: ["person", "additionalDetails", "weight"],
      width: 30,
    },
    {
      key: RosterTableColumns.PersonAdditionalDetailsHeightImperial,
      title: formatMessage({ id: "person.additionalDetails.heightImperial" }),
      dataIndex: ["person", "additionalDetails", "heightImperial"],
      width: 90,
    },
    {
      key: RosterTableColumns.PersonAdditionalDetailsWeightImperial,
      title: formatMessage({ id: "person.additionalDetails.weightImperial" }),
      dataIndex: ["person", "additionalDetails", "weightImperial"],
      width: 90,
    },
    {
      key: RosterTableColumns.PersonAdditionalDetailsRepresentation,
      title: formatMessage({ id: "person.additionalDetails.representation" }),
      dataIndex: ["person", "additionalDetails", "representation"],
      width: 90,
    },
    {
      key: RosterTableColumns.PersonAdditionalDetailsJuniorAssociationLeague,
      title: formatMessage({ id: "person.additionalDetails.juniorAssociationLeague" }),
      dataIndex: ["person", "additionalDetails", "juniorAssociationLeague"],
      width: 90,
    },
    {
      key: RosterTableColumns.PersonAdditionalDetailsHomeTown,
      title: formatMessage({ id: "person.additionalDetails.homeTown" }),
      dataIndex: ["person", "additionalDetails", "homeTown"],
      width: 90,
    },
    {
      key: RosterTableColumns.PersonAdditionalDetailsCollege,
      title: formatMessage({ id: "person.additionalDetails.college" }),
      dataIndex: ["person", "additionalDetails", "college"],
      width: 90,
    },
    {
      key: RosterTableColumns.PersonAdditionalDetailsCollegeClass,
      title: formatMessage({ id: "person.additionalDetails.collegeClass" }),
      dataIndex: ["person", "additionalDetails", "collegeClass"],
      width: 90,
    },
    {
      key: RosterTableColumns.PersonAdditionalDetailsSchool,
      title: formatMessage({ id: "person.additionalDetails.school" }),
      dataIndex: ["person", "additionalDetails", "school"],
      width: 90,
    },
    {
      key: RosterTableColumns.PersonAdditionalDetailsSchoolClass,
      title: formatMessage({ id: "person.additionalDetails.schoolClass" }),
      dataIndex: ["person", "additionalDetails", "schoolClass"],
      width: 90,
    },
    {
      key: RosterTableColumns.ExternalId,
      title: formatMessage({ id: "externalId" }),
      copyable: true,
      dataIndex: "externalId",
      width: 100,
    },
    {
      key: RosterTableColumns.Position,
      title: formatMessage({ id: "position" }),
      dataIndex: "position",
      width: 120,
      sorter: (a: TRow, b: TRow) => sortStringValues(a.position, b.position),
      render: (value: string | null, row: RosterRow) => (
        <DataTableSelectColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          options={positionOptions}
          formatValue={(position) => formatMessage({ id: `roster.position.${sport}.${position}` })}
          onSave={(position) => handleUpdateField(row, { position })}
        />
      ),
    },
  ];
};
